import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SAFETY_REPORT_ADVANCED_DETAILS_SUBTITLE, SAFETY_REPORT_ADVANCED_DETAILS_TITLE } from './safetyReportUxCopy';

type Fact = {
  label: string;
  value: string;
};

type Props = {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  reportProductName?: string;
  hasAppliedIncidentSelection: boolean;
  appliedProofScopeSummary: string;
  includedIncidentCaseCount: number | null;
  caseTotalIncidents?: string;
  visibleProofScopeFacts: Fact[];
  proofReviewCorrelationLine: string;
  packageExportSummary: string;
  onOpenTechnicalDetails: () => void;
};

export default function ProofTechnicalAppendix({
  expanded,
  onExpandedChange,
  reportProductName = 'Safety Scan',
  hasAppliedIncidentSelection,
  appliedProofScopeSummary,
  includedIncidentCaseCount,
  caseTotalIncidents,
  visibleProofScopeFacts,
  proofReviewCorrelationLine,
  packageExportSummary,
  onOpenTechnicalDetails,
}: Props) {
  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={(_, nextExpanded) => onExpandedChange(nextExpanded)}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 2.5, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: { xs: 1.25, sm: 1.5 } }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {SAFETY_REPORT_ADVANCED_DETAILS_TITLE}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.45 }}>
            {SAFETY_REPORT_ADVANCED_DETAILS_SUBTITLE.replace('Safety Scan', reportProductName || 'Safety Scan')}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {expanded ? (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              Most users can ignore this section. It is read-only and does not change Review ownership or workflow status.
            </Typography>
            <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 2.5 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.08 }}>
                    Read-only diagnostics
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
                    Current scan setup
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.5 }}>
                    {`Use this section only to confirm how the ${reportProductName} was scoped and which internal identifiers back it.`}
                  </Typography>
                </Box>
                {!hasAppliedIncidentSelection ? (
                  <Alert severity="warning" sx={{ py: 0.25 }}>
                    {`Technical details are limited until the ${reportProductName} has been built for a confirmed scan scope.`}
                  </Alert>
                ) : null}
                <Paper variant="outlined" sx={{ p: 1.15, borderRadius: 2 }}>
                  <Stack spacing={0.75}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
                      Current scan setup
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                      {appliedProofScopeSummary}
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {includedIncidentCaseCount != null ? (
                        <Chip size="small" label={`${includedIncidentCaseCount} selected incident${includedIncidentCaseCount === 1 ? '' : 's'}`} variant="outlined" />
                      ) : null}
                      {caseTotalIncidents ? <Chip size="small" label={`${caseTotalIncidents} alerts/events`} variant="outlined" /> : null}
                    </Stack>
                  </Stack>
                </Paper>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                    gap: 1,
                  }}
                >
                  {visibleProofScopeFacts.map((fact) => (
                    <Paper key={fact.label} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.02 }}>
                        {fact.label}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ mt: 0.35, fontWeight: 700, lineHeight: 1.45 }}>
                        {fact.value}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                  {proofReviewCorrelationLine}
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.15, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
                    Why this section exists
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.5 }}>
                    {`Use this only when the ${reportProductName} looks wrong, evidence seems incomplete, or you need internal identifiers to troubleshoot the export.`}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.15, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
                    Technical appendix summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.5 }}>
                    {packageExportSummary}
                  </Typography>
                </Paper>
              </Stack>
            </Paper>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                Open the full advanced-technical-details route when you need read-only diagnostics, identifier-level details, or audit context.
              </Typography>
              <Button variant="outlined" onClick={onOpenTechnicalDetails}>
                Open {SAFETY_REPORT_ADVANCED_DETAILS_TITLE}
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
}
